import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Send, MessageCircle } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate form submission
    setTimeout(() => {
      toast.success('Thank you! We will get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setLoading(false);
    }, 1000);
  };

  return (
    <div className="page-container">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="page-title" data-testid="contact-title">Get in Touch</h1>
          <p className="page-subtitle">
            Have questions? We're here to help!
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Info */}
            <div className="contact-info">
              <h2>Contact Information</h2>
              <p className="contact-intro">
                Reach out to us for any queries, support, or partnership opportunities.
              </p>

              <div className="contact-details">
                <div className="contact-detail-item">
                  <div className="contact-detail-icon">
                    <Mail size={24} />
                  </div>
                  <div className="contact-detail-text">
                    <h3>Email</h3>
                    <a href="mailto:helloelderra@gmail.com">helloelderra@gmail.com</a>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-detail-icon">
                    <Phone size={24} />
                  </div>
                  <div className="contact-detail-text">
                    <h3>Helpline</h3>
                    <a href="tel:8920455730" style={{fontSize: '1.5rem', fontWeight: '600'}}>89204-55730</a>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-detail-icon" style={{background: '#dcfce7', color: '#25D366'}}>
                    <MessageCircle size={24} />
                  </div>
                  <div className="contact-detail-text">
                    <h3>WhatsApp</h3>
                    <a href="https://wa.me/918920455730?text=Hi%20Elderra%2C%20I%20need%20help" target="_blank" rel="noopener noreferrer" style={{fontSize: '1.1rem', fontWeight: '600', color: '#25D366'}}>
                      Chat with us on WhatsApp
                    </a>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-detail-icon">
                    <MapPin size={24} />
                  </div>
                  <div className="contact-detail-text">
                    <h3>Location</h3>
                    <p>Serving 50+ cities across India</p>
                  </div>
                </div>
              </div>

              <div className="contact-hours">
                <h3>Business Hours</h3>
                <p>Monday - Saturday: 9:00 AM - 6:00 PM</p>
                <p>Sunday: Closed</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-container">
              <h2>Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="contact-name">Full Name *</label>
                  <Input
                    id="contact-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    data-testid="contact-name-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact-email">Email Address *</label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    data-testid="contact-email-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact-phone">Phone Number</label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    data-testid="contact-phone-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact-subject">Subject *</label>
                  <Input
                    id="contact-subject"
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    required
                    data-testid="contact-subject-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact-message">Message *</label>
                  <Textarea
                    id="contact-message"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    rows={6}
                    required
                    data-testid="contact-message-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="submit-button"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                >
                  <Send size={18} />
                  {loading ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2 className="section-title-center">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>What is Elderra?</h3>
              <p>
                Elderra is a WhatsApp-based digital assistant designed specifically for India's senior citizens, helping them navigate apps and services with ease.
              </p>
            </div>
            <div className="faq-item">
              <h3>How much does DigiMitra cost?</h3>
              <p>
                DigiMitra home visits are priced at ₹499 per hour of personalized tech support at your doorstep.
              </p>
            </div>
            <div className="faq-item">
              <h3>Which cities do you operate in?</h3>
              <p>
                We currently operate in 50+ cities across India and are expanding rapidly. Check our Community page for the full list.
              </p>
            </div>
            <div className="faq-item">
              <h3>Is the AI assistant available 24/7?</h3>
              <p>
                Yes! Our AI WhatsApp assistant is available round the clock to help you with your digital needs.
              </p>
            </div>
            <div className="faq-item">
              <h3>Do I need to download any apps?</h3>
              <p>
                No! Elderra works entirely through WhatsApp. No downloads, no complex interfaces - just simple chat.
              </p>
            </div>
            <div className="faq-item">
              <h3>How do I join a WhatsApp group?</h3>
              <p>
                Visit our Community page, search for your city, and click the "Join Group" button to get instant access.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;